import { fetchChiese } from '~graphql/controllers/fetchChiese';
import { fetchCimiteri } from '~graphql/controllers/fetchCimiteri';
import { fetchStrutture } from '~graphql/controllers/fetchStrutture';

/**
 * Configuration for each pointer className.
 * Maps className to fetch function and label builder.
 */
const classConfig = {
  cimiteri: {
    fetch: (ids) => fetchCimiteri({ objectId: { in: ids } }),
    getLabel: (obj) => obj.nomeAlias || obj.nome,
  },
  chiese: {
    fetch: (ids) => fetchChiese({ objectId: { in: ids } }),
    getLabel: (obj) =>
      `${obj.alias || obj.nome}${obj.citta ? ` - ${obj.citta}` : ''}`,
  },
  strutture: {
    fetch: (ids) => fetchStrutture({ objectId: { in: ids } }),
    getLabel: (obj) => obj.nome,
  },
};

/**
 * Resolves pointer fields in metadata by querying the database.
 * Groups pointers by className and executes batch queries via Promise.all.
 *
 * @param {Object} metadata - Raw metadata object with typed entries
 * @returns {Object} Metadata with resolved pointer values
 */
export async function resolveMetadataPointers(metadata) {
  if (!metadata || typeof metadata !== 'object') return {};

  // 1. Collect all pointer entries grouped by className
  const pointersByClass = {};
  const pointerKeys = [];

  for (const [key, entry] of Object.entries(metadata)) {
    if (entry?.type === 'pointer' && entry.objectId && entry.className) {
      const { objectId, className } = entry;
      if (!pointersByClass[className]) {
        pointersByClass[className] = [];
      }
      pointersByClass[className].push({ key, objectId });
      pointerKeys.push(key);
    }
  }

  // If no pointers, return metadata as-is
  if (pointerKeys.length === 0) return metadata;

  // 2. Build queries using existing controllers and execute via Promise.all
  const resolvedObjects = {};

  const queries = Object.entries(pointersByClass).map(
    async ([className, items]) => {
      const config = classConfig[className];
      if (!config) {
        console.warn(
          `[resolveMetadataPointers] Unknown className: ${className}`
        );
        return;
      }

      const ids = items.map((i) => i.objectId);

      try {
        const results = await config.fetch(ids);

        // Handle array response
        const nodes = Array.isArray(results) ? results : [];

        for (const node of nodes) {
          resolvedObjects[node.objectId] = {
            ...node,
            _label: config.getLabel(node),
          };
        }
      } catch (err) {
        console.error(
          `[resolveMetadataPointers] Failed to resolve ${className}:`,
          err
        );
      }
    }
  );

  await Promise.all(queries);

  // 3. Rebuild metadata with resolved values
  const resolved = { ...metadata };

  for (const key of pointerKeys) {
    const entry = metadata[key];
    const obj = resolvedObjects[entry.objectId];

    if (obj) {
      resolved[key] = {
        value: entry.objectId,
        label: obj._label,
        _resolved: obj, // Full object for document context
      };
    } else {
      // Fallback if object not found (deleted, etc.)
      resolved[key] = {
        value: entry.objectId,
        label: '',
      };
    }
  }

  return resolved;
}
